import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Iconify from 'src/components/iconify';
import DialogContentText from '@mui/material/DialogContentText';
import { CaySam, CaySamNhatKy } from 'src/types/caysam';
import { AddNhatKyBatchData } from 'src/api/caysam-api';
import { OptionModel } from 'src/types/api-response';
import useCaySamFilterOptions from 'src/utils/use-caysam-filter-options';
import useGetCaySam from '../hooks/use-get-caysam';

export type UpdateCaySamsResult = {
  selectedIds: Array<string | number>;
  common: { tinhTrang?: number; diem?: number };
  imagesById: Record<string | number, { hinhAnhTongQuan: string; hinhAnhChiTiet: string }>; 
};

type Props = {
  open: boolean;
  onClose: () => void;
  loading?: boolean;
  items: CaySamNhatKy[];
  onConfirm?: (data: UpdateCaySamsResult) => void;
  caySamById?: Record<string | number, CaySam | undefined>;
  filterState?: {
    tinhTrang?: number;
    diem?: number;
  };
};

export default function UpdateCaySams({ open, onClose, loading = false, items, onConfirm, caySamById = {}, filterState }: Props) {
  const { tinhTrangOptions, diemSucKhoeOptions, tuoicayOptions } = useCaySamFilterOptions();
  const { addNhatKyByCaySamIds, loadingAddNhatKy, errorAddNhatKy } = useGetCaySam();
  
  const [commonTinhTrang, setCommonTinhTrang] = React.useState<number>(0);
  const [commonDiem, setCommonDiem] = React.useState<number>(0);

  const [pickedMap, setPickedMap] = React.useState<Record<string | number, [string | undefined, string | undefined]>>({});

  React.useEffect(() => {
    if (open && filterState) {
      // Set giá trị mặc định từ filter state
      if (filterState.tinhTrang) {
        setCommonTinhTrang(filterState.tinhTrang);
      }
      if (filterState.diem) {
        setCommonDiem(filterState.diem);
      }
    } else if (!open) {
      // Reset khi đóng modal
      setCommonTinhTrang(0);
      setCommonDiem(0);
    }
  }, [open, filterState]); 

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Cập nhật sinh trưởng</DialogTitle>  
      <DialogContent dividers>
        {/* Shared options */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 ,mt:2}}>
          <FormControl fullWidth size="small">
            <InputLabel>Tình trạng</InputLabel>
            <Select value={commonTinhTrang} label="Tình trạng" onChange={(e) => setCommonTinhTrang(Number(e.target.value))}>
              {tinhTrangOptions.map((o) => (
                <MenuItem key={o.value} value={Number(o.value)}>{o.text}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small"> 
            <InputLabel>Điểm sức khỏe</InputLabel>
            <Select value={commonDiem} label="Điểm sức khỏe" onChange={(e) => setCommonDiem(Number(e.target.value))}>
              {diemSucKhoeOptions.map((o) => (
                <MenuItem key={o.value} value={Number(o.value)}>{o.text}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        {(loading || loadingAddNhatKy) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />  
          </Box>
        )}
        {errorAddNhatKy && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {errorAddNhatKy}
          </Typography>
        )}
        {!loading && !loadingAddNhatKy && items.length === 0 && (
          <Typography variant="body2" color="text.secondary">Không có cây nào theo bộ lọc hiện tại.</Typography>
        )} 
        {!loading && !loadingAddNhatKy && items.length > 0 && (   
          <Stack spacing={2}>
            {items.map((it) => {
                const cs: CaySam | undefined = caySamById[it.caySamId || it.id] || it.caySam || undefined;
                // Lấy bản ghi hiển thị: ưu tiên item hiện tại; nếu thiếu, lấy nhật ký mới nhất của cây tương ứng
                const latestFromCay = cs?.caySamNhatKys && cs.caySamNhatKys.length > 0
                  ? cs.caySamNhatKys[cs.caySamNhatKys.length - 1]
                  : undefined;
                const nkShow: CaySamNhatKy = {
                  id: it.id,
                  caySamId: it.caySamId || (cs?.caySam_ID as any),
                  taiKhoanId: it.taiKhoanId as any,
                  ngayGhi: it.ngayGhi || (latestFromCay?.ngayGhi as any),
                  hinhAnhTongQuan: it.hinhAnhTongQuan || latestFromCay?.hinhAnhTongQuan || '',
                  hinhAnhChiTiet: it.hinhAnhChiTiet || latestFromCay?.hinhAnhChiTiet || '',
                  soLa: it.soLa ?? latestFromCay?.soLa ?? 0,
                  diemSucKhoe: it.diemSucKhoe ?? latestFromCay?.diemSucKhoe ?? 0,
                  tinhTrang: it.tinhTrang ?? latestFromCay?.tinhTrang ?? 0,
                  caySam: cs,
                  htTaiKhoan: it.htTaiKhoan as any,
                } as CaySamNhatKy;  
                return (
              <Box key={it.id} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, width: '50%' }}>
                  {[0, 1].map((idx) => (
                    <InlineImagePicker
                      key={idx}
                      width="100%"   
                      height={{ xs: 80, sm: 100, md: 120 }}  
                      value={pickedMap[it.id]?.[idx] as string | undefined}
                      onChange={(src) =>  
                        setPickedMap((prev) => {
                          const current = prev[it.id] || [undefined, undefined];
                          const next: [string | undefined, string | undefined] = idx === 0 ? [src, current[1]] : [current[0], src];
                          return { ...prev, [it.id]: next };
                        }) 
                      }
                    /> 
                  ))}
                </Box>
                <Box sx={{ flex: 1 }}>  
                  <Typography variant="subtitle2">{cs?.viTriTrongLo || '---'}</Typography>
                  <Typography variant="body2" color="text.secondary">Mã: {cs?.maCaySam || it.caySamId || `#${it.id}`}</Typography>
                  <Typography variant="body2" color="text.secondary">Tuổi: {getText(tuoicayOptions, cs?.tuoiCay_ID)}</Typography>
                  <Typography variant="body2" color="text.secondary">Tình trạng: {getText(tinhTrangOptions, nkShow.tinhTrang)}</Typography>
                  <Typography variant="body2" color="text.secondary">Sức khỏe: {nkShow.diemSucKhoe ?? '-'} | Số lá: {nkShow.soLa ?? '-'}</Typography>
                </Box>
              </Box>
            );})}
            <Divider />
          </Stack>
        )} 
      </DialogContent> 
      <DialogActions>
        <Button onClick={onClose}>Đóng</Button>
        <Button
          variant="contained"
          onClick={async () => {
            try {
              // Chuẩn bị dữ liệu cho API
              const imagesById: UpdateCaySamsResult['imagesById'] = {};
              const files: File[] = [];
              
              // Sắp xếp items theo ID để đảm bảo thứ tự files đúng
              const sortedItems = [...items].sort((a, b) => {
                const idA = a.caySamId || a.id;
                const idB = b.caySamId || b.id;
                return String(idA).localeCompare(String(idB));
              });
              
              sortedItems.forEach((it) => {
                const picked = pickedMap[it.id];
                imagesById[it.id] = {
                  hinhAnhTongQuan: picked?.[0] || '',
                  hinhAnhChiTiet: picked?.[1] || '',
                };

                // Thêm files theo thứ tự: ảnh tổng quan, ảnh chi tiết
                if (picked?.[0]) {
                  // Convert base64 to File với tên gốc và giữ nguyên extension
                  const file1 = base64ToFile(picked[0], `tongquan_${it.id}`);
                  if (file1) {
                    files.push(file1);
                  }
                }
                if (picked?.[1]) {
                  const file2 = base64ToFile(picked[1], `chitiet_${it.id}`);
                  if (file2) {
                    files.push(file2);
                  }
                }
              });

              // Tạo dữ liệu batch theo format API
              const batchData: AddNhatKyBatchData = {
                Ids: sortedItems.map((it) => String(it.caySamId || it.id)),
                NgayGhi: new Date().toISOString(),
                TinhTrang: commonTinhTrang || undefined,
                DiemSucKhoe: commonDiem || undefined,
                SoLa: 0, // TODO: Thêm field SoLa vào form
              };

              // Gọi API sử dụng hook
              const success = await addNhatKyByCaySamIds(batchData, files);
              
              if (success) {
                console.log('Update growth successful!');
                onConfirm?.({
                  selectedIds: items.map((it) => it.id),
                  common: {
                    tinhTrang: commonTinhTrang || undefined,
                    diem: commonDiem || undefined,
                  },
                  imagesById, 
                });
                // Đóng modal sau khi thành công
                onClose();
              } else {
                console.error('API Error:', errorAddNhatKy);
                // TODO: Hiển thị thông báo lỗi
              }
            } catch (error) {
              console.error('Error updating cays:', error);
              // TODO: Hiển thị thông báo lỗi
            }
          }}
          disabled={loading || loadingAddNhatKy || items.length === 0}
        >
          Xác nhận
        </Button>
      </DialogActions>   

      {/* removed modal picker; images are directly clickable in list */}
    </Dialog>
  );
}

// Local component: a single slot with "+" frame and file input
type SlotPickerProps = {
  label: string;
  value?: string;
  onChange: (src: string) => void;
};

function InlineImagePicker({ value, onChange, width = 72, height = 48 }: { value?: string; onChange: (src: string) => void; width?: number | string; height?: number | string | Record<string, number> }) {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [fileSize, setFileSize] = React.useState<string | null>(null);
  const [originalFile, setOriginalFile] = React.useState<File | null>(null);
  
  const pick = () => {
    setError(null);
    inputRef.current?.click();
  };

  const validateFile = (file: File): string | null => {
    // Kiểm tra loại file
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      return 'Chỉ chấp nhận file ảnh định dạng .jpg, .jpeg, .png';
    }

    // Kiểm tra kích thước file (5MB = 5 * 1024 * 1024 bytes)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      return `File quá lớn (${fileSizeMB}MB). Kích thước tối đa: 5MB`;
    }

    return null;
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {value ? (
        <Box component="img" src={value} onClick={pick} alt="img" sx={{ width, height, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider', cursor: 'pointer' }} />
      ) : (
        <Box onClick={pick} sx={{ width, height, borderRadius: 1, border: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', cursor: 'pointer' }}>
          <Iconify icon="solar:add-circle-bold" />
        </Box> 
      )}
      {error && (
        <Typography variant="caption" color="error" sx={{ position: 'absolute', bottom: -20, left: 0, right: 0, textAlign: 'center', fontSize: '0.7rem' }}>
          {error}
        </Typography>
      )}
      {fileSize && !error && (
        <Typography variant="caption" color="success.main" sx={{ position: 'absolute', bottom: -20, left: 0, right: 0, textAlign: 'center', fontSize: '0.7rem' }}>
          {fileSize}
        </Typography>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png"
        hidden
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          
          // Validate file type and size
          const validationError = validateFile(file);
          if (validationError) {
            setError(validationError);
            return;
          }

          try {
            // Compress image if it's larger than 1MB
            let processedFile = file;
            if (file.size > 1024 * 1024) { // 1MB
              setError('Đang nén ảnh...');
              processedFile = await compressImage(file, 1920, 0.8);
              setError(null);
            }

            // Show file size info
            const sizeMB = (processedFile.size / (1024 * 1024)).toFixed(2) ;
            setFileSize(`${sizeMB}MB`); 

            const reader = new FileReader();
            reader.onload = () => onChange(String(reader.result));
            reader.readAsDataURL(processedFile);
          } catch (imgError) {
            console.error('Error processing image:', imgError);
            setError('Lỗi khi xử lý ảnh');
          }
        }}
      /> 
    </Box> 
  );
}

function getText(options : OptionModel[], v?: number | string | null) {
  const found = options.find((o) => String(o.value) === String(v));
  return found?.text || (v != null ? String(v) : '-');
}

// Compress image to reduce file size
function compressImage(file: File, maxWidth: number = 1920, quality: number = 0.8): Promise<File> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width; 
      canvas.height = height;
      
      // Draw and compress
      ctx?.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else {
            resolve(file); // Fallback to original file
          }
        },
        'image/jpeg',
        quality
      );
    };
    
    img.src = URL.createObjectURL(file);
  });
}

// Convert base64 string to File
function base64ToFile(base64: string, filename: string): File | null {
  try {
    // Check if it's a valid base64 string
    if (!base64.startsWith('data:')) return null;
    
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
     
    // Get file extension from MIME type
    const getExtension = (mimeType: string): string => {
      const mimeToExt: Record<string, string> = {
        'image/jpeg': '.jpg',
        'image/jpg': '.jpg', 
        'image/png': '.png',
        'image/gif': '.gif',
        'image/webp': '.webp'
      };  
      return mimeToExt[mimeType] || '.jpg';
    };
    
    const extension = getExtension(mime);
    const fullFilename = filename.includes('.') ? filename : `${filename}${extension}`;
    
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    
    while (n > 0) {
      n -= 1;
      u8arr[n] = bstr.charCodeAt(n);
    }
    
    return new File([u8arr], fullFilename, { type: mime });
  } catch (error) {
    console.error('Error converting base64 to file:', error);
    return null;
  }
}


